import type { Camera, Size } from '@/types';
import { worldToScreen } from '@/core/coordinates/coordinateSystem';
import { computeGridModel, firstLine, type GridModel } from './gridModel';

export interface GridPalette {
  readonly minor: string;
  readonly major: string;
  readonly axis: string;
  readonly origin: string;
}

/**
 * Minimal drawing surface so the renderer is testable without a real canvas.
 * CanvasRenderingContext2D satisfies it structurally.
 */
export interface GridContext2D {
  clearRect(x: number, y: number, w: number, h: number): void;
  beginPath(): void;
  moveTo(x: number, y: number): void;
  lineTo(x: number, y: number): void;
  stroke(): void;
  arc(x: number, y: number, r: number, start: number, end: number): void;
  fill(): void;
  setTransform(a: number, b: number, c: number, d: number, e: number, f: number): void;
  strokeStyle: string | CanvasGradient | CanvasPattern;
  fillStyle: string | CanvasGradient | CanvasPattern;
  lineWidth: number;
  globalAlpha: number;
}

/** True when `value` lies on a multiple of `period` (tolerant of float accumulation). */
const onMultiple = (value: number, period: number, tolerance: number): boolean =>
  Math.abs(value - Math.round(value / period) * period) < tolerance;

const strokeLevel = (
  ctx: GridContext2D,
  model: GridModel,
  level: 'minor' | 'major',
  camera: Camera,
  color: string,
  skip: number | null,
): void => {
  const { spacing, alpha } = model[level];
  if (alpha <= 0.01) return;
  const { bounds } = model;
  ctx.globalAlpha = alpha;
  ctx.strokeStyle = color;
  ctx.lineWidth = 1;
  ctx.beginPath();
  for (let x = firstLine(bounds.minX, spacing); x <= bounds.maxX; x += spacing) {
    if (skip !== null && onMultiple(x, skip, spacing / 2)) continue;
    const sx = Math.round(worldToScreen({ x, y: 0 }, camera).x) + 0.5;
    ctx.moveTo(sx, 0);
    ctx.lineTo(sx, (bounds.maxY - bounds.minY) * camera.zoom);
  }
  for (let y = firstLine(bounds.minY, spacing); y <= bounds.maxY; y += spacing) {
    if (skip !== null && onMultiple(y, skip, spacing / 2)) continue;
    const sy = Math.round(worldToScreen({ x: 0, y }, camera).y) + 0.5;
    ctx.moveTo(0, sy);
    ctx.lineTo((bounds.maxX - bounds.minX) * camera.zoom, sy);
  }
  ctx.stroke();
};

/**
 * Draws the adaptive grid, origin axes, and origin marker for one frame. The caller
 * has already sized the backing store for device pixel ratio; `dpr` is applied here
 * as a transform so all coordinates below are CSS pixels.
 */
export const drawGrid = (
  ctx: GridContext2D,
  camera: Camera,
  viewport: Size,
  dpr: number,
  palette: GridPalette,
): GridModel => {
  const model = computeGridModel(camera, viewport);
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, viewport.width, viewport.height);

  strokeLevel(ctx, model, 'minor', camera, palette.minor, model.major.spacing);
  strokeLevel(ctx, model, 'major', camera, palette.major, null);

  if (model.showOrigin) {
    const o = worldToScreen({ x: 0, y: 0 }, camera);
    ctx.globalAlpha = 0.55;
    ctx.strokeStyle = palette.axis;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(Math.round(o.x) + 0.5, 0);
    ctx.lineTo(Math.round(o.x) + 0.5, viewport.height);
    ctx.moveTo(0, Math.round(o.y) + 0.5);
    ctx.lineTo(viewport.width, Math.round(o.y) + 0.5);
    ctx.stroke();

    ctx.globalAlpha = 1;
    ctx.fillStyle = palette.origin;
    ctx.beginPath();
    ctx.arc(o.x, o.y, 3, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.globalAlpha = 1;
  return model;
};
